/**
 * Copyright © 2026 Adnuntius AS.
 */
import {Injectable} from '@angular/core';
import {map, Observable} from 'rxjs';

import {AdnApiClientService} from '../../common/controller/adn-api-client.service';
import {
  AdminObjectState,
  SignalAdminResourceApiBase,
  SignalAdminResourcePage
} from '../../common/controller/signal-admin-resource-api';
import {EmailTemplateModel, EmailTemplateTeam} from '../email-template/email-template-api.service';

export interface EmailTranslationModel {
  id: string;
  name?: string;
  description?: string;
  messageDefinition?: string;
  team?: EmailTemplateTeam | null;
  applications?: string[];
  locale?: string;
  userInterface?: string;
  emailTemplate?: string | EmailTemplateModel | null;
  replyTo?: string;
  emailSubject?: string;
  emailHeading?: string;
  emailBody?: string;
  emailFooter?: string;
  specifyApplications?: boolean;
  specifyLocale?: boolean;
  specifyInterface?: boolean;
  specifyEmailSubject?: boolean;
  specifyEmailHeading?: boolean;
  specifyEmailBody?: boolean;
  specifyEmailFooter?: boolean;
  objectState?: AdminObjectState;
  validationWarnings?: any[];
}

export interface EmailUserInterface {
  code: string;
  url: string;
}

@Injectable({providedIn: 'root'})
export class EmailTranslationApiService extends SignalAdminResourceApiBase<EmailTranslationModel> {
  constructor(client: AdnApiClientService) {
    super(client, 'emailtranslations', {showEditableOnly: true});
  }

  listTeams(params: Record<string, any>): Observable<SignalAdminResourcePage<EmailTemplateTeam>> {
    return this.client.request<SignalAdminResourcePage<EmailTemplateTeam>>({
      method: 'GET',
      url: this.resourceUri('teams'),
      // Same defaults the old Team.query() resource applied.
      params: {includeActive: true, includeInactive: false, includeHidden: false, orderBy: 'name', pageSize: 1001, ...params}
    });
  }

  listEmailTemplates(): Observable<SignalAdminResourcePage<EmailTemplateModel>> {
    return this.client.request<SignalAdminResourcePage<EmailTemplateModel>>({
      method: 'GET',
      url: this.resourceUri('emailtemplates'),
      params: {includeActive: true, includeInactive: false, includeHidden: false, orderBy: 'name', pageSize: 1001, showEditableOnly: true}
    });
  }

  getDefaultTranslations(): Observable<any[]> {
    return this.client.request<SignalAdminResourcePage<any>>({
      method: 'GET',
      url: this.resourceUri('emailtranslationdefaults'),
      params: {context: null}
    }).pipe(map((page) => page.results || []));
  }

  getUserInterfaces(networkId: string): Observable<SignalAdminResourcePage<EmailUserInterface>> {
    return this.client.request<SignalAdminResourcePage<EmailUserInterface>>({
      method: 'GET',
      url: this.resourceUri('emailtranslationdefaults/userInterfaces'),
      params: {context: networkId}
    });
  }

  sendTest(id: string, emailAddress: string): Observable<void> {
    return this.client.request<void>({
      method: 'GET',
      url: this.resourceUri(`emailtranslations/sendtest/${encodeURIComponent(id)}`),
      params: {to: emailAddress}
    });
  }

  listOptions(params: Record<string, any> = {}): Observable<SignalAdminResourcePage<EmailTranslationModel>> {
    return this.client.request<SignalAdminResourcePage<EmailTranslationModel>>({
      method: 'GET',
      url: this.resourceUri('emailtranslations'),
      params: {showEditableOnly: true, ...params}
    });
  }
}
